import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { styled, useTheme } from '@mui/material/styles';
import { Box } from '@mui/system';
import { Typography, Paper, CircularProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { PageHeader } from 'components/molecules/PageHeader/PageHeader';
import { selectStrategyById } from 'redux/strategy/strategySlice';
import { routes } from 'routes';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Button from '@mui/material/Button';

// Styled components
const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  '& .MuiDataGrid-main': {
    backgroundColor: 'white'
  },
  '& .MuiDataGrid-row': {
    backgroundColor: 'white'
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: theme.palette.grey[100]
  },
  '& .MuiDataGrid-footerContainer': {
    backgroundColor: 'transparent'
  }
}));

/**
 * StrategySubscribers Component
 * Displays a list of subscribers for a strategy
 */
export default function StrategySubscribers() {
  const theme = useTheme();
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const strategyId = params.strategyId;
  const strategy = useSelector((state) => selectStrategyById(state, strategyId));

  // Redirect if strategy doesn't exist or user is not the owner
  useEffect(() => {
    if (strategy && strategy.owned === false) {
      navigate(routes.strategyDetails.replace(':strategyId', strategyId));
    }
  }, [strategy, strategyId, navigate]);

  // Define columns for the subscribers table
  const columns = [
    { 
      field: 'email', 
      headerName: 'Email', 
      flex: 1, 
      minWidth: 200 
    },
    { 
      field: 'autotrading_status_display', 
      headerName: 'Auto-Trading Status', 
      flex: 1, 
      minWidth: 150,
      valueGetter: (params) => params.row.autotrading_status_display || 'Inactive'
    },
    { 
      field: 'autotrading_started', 
      headerName: 'Auto-Trading Start Date', 
      flex: 1, 
      minWidth: 200,
      valueFormatter: (params) => {
        return params.value ? new Date(params.value).toLocaleString() : 'N/A';
      }
    },
    { 
      field: 'subscription_created', 
      headerName: 'Subscription Date', 
      flex: 1, 
      minWidth: 200,
      valueFormatter: (params) => {
        return new Date(params.value).toLocaleString();
      }
    }
  ];

  // If strategy is loading or doesn't exist
  if (!strategy) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  // If strategy exists but has no subscribers
  const subscribers = strategy.subscribers || [];

  return (
    <Box sx={{ mb: 5 }}>
      <Box sx={{ mb: 3 }}>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate(routes.strategyDetails.replace(':strategyId', strategyId))}
          sx={{ mb: 2 }}
        >
          Back to Strategy
        </Button>
        <PageHeader 
          text1="Subscribers"
          description={`View subscribers to your ${strategy.name}`}
        />
      </Box>

      <Paper sx={{ p: 3, mb: 4 }}>
        {subscribers.length > 0 ? (
          <StyledDataGrid
            rows={subscribers.map((subscriber, index) => ({
              id: index, // Using index as id since subscribers might not have unique ids
              ...subscriber
            }))}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
              sorting: {
                sortModel: [{ field: 'subscription_created', sort: 'desc' }],
              },
            }}
            pageSizeOptions={[5, 10, 25]}
            autoHeight
          />
        ) : (
          <Typography variant="body1">No subscribers found for this strategy.</Typography>
        )}
      </Paper>
    </Box>
  );
} 